import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SKILL_TABS, type SkillStatus, type SkillTabId } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, cx, Dialog, ErrorText, Field, Input, PageHeader, Pagination, Spinner } from "../components/ui.tsx";
import { keepPreviousData, usePaging } from "../hooks.ts";
import { MarkdownDoc } from "../components/Markdown.tsx";
import { AudienceBadge } from "../components/knowledge.tsx";
import { Icon } from "../components/icons.tsx";

type Row = Awaited<ReturnType<typeof loadList>>["items"][number];
const load = () => unwrap(api.admin.skills.$get());
const loadList = (p: { tab: SkillTabId; page: number; pageSize: number; q: string }) =>
  unwrap(api.admin.skills.list.$get({ query: { tab: p.tab, page: String(p.page), pageSize: String(p.pageSize), ...(p.q && { q: p.q }) } }));

export const statusBadge: Record<SkillStatus, { tone: "good" | "accent" | "bad" | "neutral"; label: string }> = {
  official: { tone: "accent", label: "Official" },
  shared: { tone: "good", label: "Shared" },
  pending: { tone: "accent", label: "Needs review" },
  blocked: { tone: "bad", label: "Sensitive" },
  private: { tone: "neutral", label: "Private" },
  hidden: { tone: "neutral", label: "Hidden by admin" },
  superseded: { tone: "neutral", label: "Superseded" },
  unshared: { tone: "neutral", label: "Only in their assistant" },
};

export function SkillsAdminPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["skills-admin"], queryFn: load });
  const [tab, setTab] = useState<SkillTabId>("official");
  const paging = usePaging([tab]);
  const { page, pageSize } = paging;
  const list = useQuery({
    queryKey: ["skills-admin", "list", tab, page, pageSize, paging.q],
    queryFn: () => loadList({ tab, page, pageSize, q: paging.q }),
    placeholderData: keepPreviousData,
  });
  const total = list.data?.total;
  useEffect(() => void paging.clamp(total), [total]); // eslint-disable-line react-hooks/exhaustive-deps
  const [editing, setEditing] = useState<{ name: string; isNew: boolean } | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["skills-admin"] });
  const refresh = useMutation({ mutationFn: () => unwrap(api.admin.skills.refresh.$post()), onSuccess: invalidate });
  const act = useMutation({
    mutationFn: async (a: { kind: "adopt" | "share-group" | "share-company" | "hidden" | "delete"; row: Row }) => {
      if (a.kind === "adopt") return unwrap(api.admin.skills[":id"].adopt.$post({ param: { id: a.row.id } }));
      if (a.kind === "delete") return unwrap(api.admin.skills.official[":name"].$delete({ param: { name: a.row.name } }));
      if (a.kind === "hidden") return unwrap(api.admin.skills[":id"].status.$post({ param: { id: a.row.id }, json: { status: "hidden" } }));
      return unwrap(api.admin.skills[":id"].status.$post({ param: { id: a.row.id }, json: { status: "shared", audience: a.kind === "share-group" ? "group" : "company" } }));
    },
    onSuccess: invalidate,
  });

  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const rows = list.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Skills"
        description="Skills are reusable instructions assistants follow. Official skills go to everyone and win over any skill with the same name. Skills people's assistants create are checked for sensitive data, then shared in two tiers — within the person's group, or with the whole company — as the group's sharing level and rules decide."
        actions={
          <>
            {refresh.data && <span className="text-xs text-[var(--muted)]">{refresh.data.skipped ?? `Checked ${refresh.data.users} people · ${refresh.data.scanned} scanned · ${refresh.data.synced} assistants updated`}</span>}
            <Button disabled={refresh.isPending} onClick={() => refresh.mutate()}>{refresh.isPending ? "Scanning…" : "Scan now"}</Button>
            <Button variant="primary" onClick={() => setEditing({ name: "", isNew: true })}>New official skill</Button>
          </>
        }
      />
      <Card className="mb-6 flex flex-wrap items-center justify-between gap-3 p-4 text-sm">
        <span>
          Sharing people's skills is <b>{q.data.settings.sharingEnabled ? "on" : "off"}</b>
          {q.data.settings.sharingEnabled && q.data.settings.requireApproval ? ", and each one waits for your review first." : "."}
          {q.data.settings.sharingEnabled && q.data.groupsAllowedToPublish === 0 && " No group shares skills yet."}
        </span>
        <Link to="/admin/knowledge/rules" className="inline-flex items-center gap-1.5 text-[var(--color-accent)] hover:underline">
          Switches and rules: Sharing rules <Icon name="arrowRight" size={14} />
        </Link>
      </Card>
      <ErrorText error={refresh.error ?? act.error} />

      <Card className="mt-6 overflow-hidden">
        <div className="flex flex-wrap gap-1 border-b border-[var(--line)] px-4 py-3">
          {SKILL_TABS.map((t) => {
            const n = q.data.counts[t.id];
            return (
              <button key={t.id} onClick={() => setTab(t.id)} className={cx("rounded-md px-3 py-1.5 text-sm", tab === t.id ? "bg-[var(--panel-2)] font-medium" : "text-[var(--muted)] hover:text-[var(--text)]")}>
                {t.label} {n > 0 && <span className="text-xs text-[var(--muted)]">{n}</span>}
              </button>
            );
          })}
        </div>
        <div className="border-b border-[var(--line)] px-4 py-3">
          <Input value={paging.search} onChange={(e) => paging.setSearch(e.target.value)} placeholder="Search by name, description, person, email or group…" className="max-w-md" />
        </div>
        {list.isPending ? (
          <div className="grid place-items-center py-10"><Spinner /></div>
        ) : list.error ? (
          <div className="p-4"><ErrorText error={list.error} /></div>
        ) : rows.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-[var(--muted)]">{paging.q ? "No skills match that search." : tab === "official" ? "No official skills yet." : "Nothing here."}</p>
        ) : (
          <ul className={cx(list.isPlaceholderData && "opacity-60")}>
            {rows.map((r) => (
              <li key={r.id} className="flex flex-wrap items-start gap-3 border-b border-[var(--line)] px-4 py-3 last:border-0">
                <div className="min-w-0 flex-1 basis-60">
                  <div className="flex flex-wrap items-center gap-2">
                    <button className="font-mono text-sm font-medium hover:underline" onClick={() => setViewing(r.id)}>{r.name}</button>
                    <Badge tone={statusBadge[r.status].tone}>{statusBadge[r.status].label}</Badge>
                    {(r.status === "shared" || r.status === "pending") && <AudienceBadge audience={r.audience} groupName={r.owner?.groupName} />}
                    {r.status === "shared" && !r.live && <Badge>Name already taken</Badge>}
                  </div>
                  {r.description && <div className="mt-0.5 text-sm text-[var(--muted)]">{r.description}</div>}
                  <div className="mt-1 space-y-0.5 text-xs text-[var(--muted)]">
                    <div>
                      <span className="font-medium text-[var(--text)]">Source:</span>{" "}
                      {r.owner ? <>{r.owner.name} · {r.owner.email} · {r.owner.groupName ?? "no group"}</> : <>Official{r.updatedBy ? <> · published by {r.updatedBy}</> : null}</>}
                    </div>
                    <div>{r.fileCount} file{r.fileCount === 1 ? "" : "s"} · updated {new Date(r.updatedAt).toLocaleString()}</div>
                  </div>
                  <ReviewNote row={r} />
                  <ScanNotes scan={r.scan} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {r.status === "official" ? (
                    <>
                      <Button onClick={() => setEditing({ name: r.name, isNew: false })}>Edit</Button>
                      <Button variant="danger" disabled={act.isPending} onClick={() => confirm(`Delete the official skill "${r.name}" from every assistant?`) && act.mutate({ kind: "delete", row: r })}>Delete</Button>
                    </>
                  ) : (
                    <>
                      {(r.status === "pending" || r.status === "hidden" || (r.status === "blocked" && !r.scan?.findings.length)) && (
                        <>
                          <Button disabled={act.isPending} onClick={() => act.mutate({ kind: "share-group", row: r })} title={r.owner?.groupName ? `Share with ${r.owner.groupName} only` : undefined}>
                            {r.status === "blocked" ? "Share with group anyway" : "Share with group"}
                          </Button>
                          <Button disabled={act.isPending} onClick={() => act.mutate({ kind: "share-company", row: r })}>{r.status === "blocked" ? "Share with company anyway" : "Share with company"}</Button>
                        </>
                      )}
                      {r.status === "shared" && (
                        <Button disabled={act.isPending} onClick={() => act.mutate({ kind: r.audience === "group" ? "share-company" : "share-group", row: r })}>
                          {r.audience === "group" ? "Widen to company" : "Keep to group"}
                        </Button>
                      )}
                      {(r.status === "shared" || r.status === "pending") && (
                        <Button disabled={act.isPending} onClick={() => act.mutate({ kind: "hidden", row: r })}>Hide</Button>
                      )}
                      {r.status !== "private" && !r.scan?.findings.length && (
                        <Button disabled={act.isPending} onClick={() => confirm(`Make "${r.name}" an official skill? It will replace any same-named skill in every assistant.`) && act.mutate({ kind: "adopt", row: r })}>Make official</Button>
                      )}
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        <Pagination page={page} pageSize={pageSize} total={total ?? 0} onPage={paging.setPage} onPageSize={paging.setPageSize} busy={list.isFetching} />
      </Card>

      {editing && <Editor key={editing.name || "new"} name={editing.name} isNew={editing.isNew} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); invalidate(); }} />}
      {viewing && <Viewer id={viewing} admin onClose={() => setViewing(null)} />}
    </>
  );
}

/** The verdict of the automatic check against the sharing rules (Knowledge → Sharing rules), when it is clean. */
function ReviewNote({ row }: { row: Row }) {
  if (!row.owner) return null;
  const { scan, status } = row;
  if (!scan) return status === "pending" || status === "shared" ? <div className="mt-1.5 text-xs text-[var(--muted)]">Not checked against the sharing rules yet.</div> : null;
  if (scan.findings.length || scan.review?.sensitive || scan.error) return null;
  return (
    <div className="mt-1.5 text-xs text-emerald-800">
      Checked against the sharing rules on {new Date(scan.scannedAt).toLocaleDateString()}: nothing sensitive found.
      {scan.review?.reasons.length ? <span className="text-[var(--muted)]"> {scan.review.reasons.join(" ")}</span> : null}
      {scan.review?.audience && scan.review.audienceReason ? (
        <span className="text-[var(--muted)]">
          {" "}
          The group's rules put it {scan.review.audience === "group" ? "with the group" : "company-wide"}: {scan.review.audienceReason}
        </span>
      ) : null}
    </div>
  );
}

export function ScanNotes({ scan }: { scan: Row["scan"] }) {
  if (!scan) return null;
  const notes = [...scan.findings, ...(scan.review?.sensitive ? scan.review.reasons : [])];
  if (scan.error) notes.push(`Review could not run: ${scan.error}. It will be retried.`);
  if (!notes.length) return null;
  return (
    <ul className="mt-2 space-y-0.5 rounded-md bg-red-500/5 px-3 py-2 text-xs text-red-800">
      {notes.map((n, i) => <li key={i}>{n}</li>)}
    </ul>
  );
}

const template = (name: string) => `---
name: ${name || "my-skill"}
description: One sentence on when to use this skill — assistants read this to decide.
---

# ${name || "My skill"}

## When to use

## Steps

1.
`;

function Editor({ name: initial, isNew, onClose, onSaved }: { name: string; isNew: boolean; onClose: () => void; onSaved: () => void }) {
  const q = useQuery({
    queryKey: ["skill-official", initial],
    enabled: !isNew,
    queryFn: () => unwrap(api.admin.skills.official[":name"].$get({ param: { name: initial } })),
  });
  const [name, setName] = useState(initial);
  const [files, setFiles] = useState<{ path: string; text: string }[] | null>(isNew ? [{ path: "SKILL.md", text: template("") }] : null);
  const [binary, setBinary] = useState<string[]>([]);
  useEffect(() => {
    if (q.data && !files) {
      setFiles(q.data.files.filter((f) => f.text !== null).map((f) => ({ path: f.path, text: f.text! })));
      setBinary(q.data.files.filter((f) => f.text === null).map((f) => f.path));
    }
  }, [q.data, files]);
  const save = useMutation({
    mutationFn: () => unwrap(api.admin.skills.official.$put({ json: { name: name.trim(), files: Object.fromEntries(files!.map((f) => [f.path.trim(), f.text])) } })),
    onSuccess: onSaved,
  });
  const set = (i: number, p: Partial<{ path: string; text: string }>) => setFiles(files!.map((f, j) => (j === i ? { ...f, ...p } : f)));

  return (
    <Dialog open onClose={onClose} title={isNew ? "New official skill" : `Edit ${initial}`} wide>
      {!files ? (
        <div className="grid place-items-center py-10"><Spinner /></div>
      ) : (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <Field label="Name" hint="Lowercase, digits, dashes. Must match name: in SKILL.md.">
            <Input
              required
              disabled={!isNew}
              value={name}
              onChange={(e) => {
                const v = e.target.value.toLowerCase();
                // Keep the template's frontmatter name in step while the admin types a new name.
                if (isNew && files[0]?.path === "SKILL.md") set(0, { text: files[0].text.replace(/^name: .*$/m, `name: ${v || "my-skill"}`) });
                setName(v);
              }}
              className="font-mono"
            />
          </Field>
          {files.map((f, i) => (
            <div key={i}>
              <div className="mb-1 flex items-center gap-2">
                <Input value={f.path} disabled={f.path === "SKILL.md" && i === 0} onChange={(e) => set(i, { path: e.target.value })} className="font-mono text-xs" />
                {i > 0 && <Button type="button" variant="danger" onClick={() => setFiles(files.filter((_, j) => j !== i))}>Remove</Button>}
              </div>
              <textarea rows={i === 0 ? 16 : 8} value={f.text} onChange={(e) => set(i, { text: e.target.value })} className="w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20" />
            </div>
          ))}
          {binary.length > 0 && <p className="text-xs text-[var(--muted)]">Kept as is: {binary.join(", ")}</p>}
          <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => setFiles([...files, { path: `references/notes-${files.length}.md`, text: "" }])}>+ Add a file</button>
          <ErrorText error={save.error} />
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={save.isPending || !name}>{save.isPending ? "Publishing…" : "Publish to everyone"}</Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

export function Viewer({ id, admin, onClose }: { id: string; admin?: boolean; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["skill-view", id, admin],
    queryFn: () => (admin ? unwrap(api.admin.skills[":id"].$get({ param: { id } })) : unwrap(api.me.skills[":id"].$get({ param: { id } }))),
  });
  const [tab, setTab] = useState(0);
  const file = q.data?.files[tab];
  return (
    <Dialog open onClose={onClose} title={q.data?.name ?? "Skill"} wide="xl">
      {q.isPending ? (
        <div className="grid place-items-center py-10"><Spinner /></div>
      ) : q.error ? (
        <ErrorText error={q.error} />
      ) : (
        <div className="space-y-4">
          {q.data.files.length > 1 && (
            <div className="flex flex-wrap gap-1 border-b border-[var(--line)] pb-2">
              {q.data.files.map((f, i) => (
                <button key={f.path} onClick={() => setTab(i)} className={cx("rounded-md px-2.5 py-1 font-mono text-xs", i === tab ? "bg-[var(--panel-2)] font-medium" : "text-[var(--muted)] hover:text-[var(--text)]")}>
                  {f.path}
                </button>
              ))}
            </div>
          )}
          {file &&
            (file.text === null ? (
              <div className="rounded-lg bg-[var(--panel-2)] px-3 py-2 text-xs text-[var(--muted)]">{file.path} · binary file · {file.size.toLocaleString()} bytes</div>
            ) : /\.(md|markdown)$/i.test(file.path) ? (
              <MarkdownDoc key={file.path} text={file.text} actions={<span className="font-mono text-xs text-[var(--muted)]">{file.path}</span>} />
            ) : (
              <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg bg-[var(--panel-2)] px-4 py-3 font-mono text-xs leading-relaxed">{file.text}</pre>
            ))}
          <div className="flex justify-end"><Button onClick={onClose}>Close</Button></div>
        </div>
      )}
    </Dialog>
  );
}
